import {
  FileDirectoryIcon,
  SearchIcon,
  SyncIcon
} from "@primer/octicons-react";
import { Box } from "@primer/react";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  FO_BAOCAO_AN_HOC_SINH_API_END_POINT,
  foDangKyAnHocSinhApi
} from "../../../api/foDangKyAnHocSinhApi";
import ComboboxKhoi from "../../../components/combobox-khoi/ComboboxKhoi";
import { ComboboxTruong } from "../../../components/combobox-truong";
import HocSinhShortInfo from "../../../components/hoc-sinh/HocSinhShortInfo";
import Button from "../../../components/ui/button";
import DataTable from "../../../components/ui/data-table";
import { IColumn } from "../../../components/ui/data-table/DataTable";
import { default as MyText, default as Text } from "../../../components/ui/text";
import MyTextInput from "../../../components/ui/text-input";
import TextWithEllipse from "../../../components/ui/text-with-ellipse";
import { useCommonContext } from "../../../contexts/common";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";
import { useDangKyAnHocSinh } from "../../../hooks/useDangKyAnHocSinh";
import { ePageBaseStatus } from "../../../models/ePageBaseStatus";
import { IFoThucDonItemRequest } from "../../../models/request/thuc-don/IFoThucDonItemRequest";
dayjs.extend(isSameOrBefore);

const BaoCaoThangPage = () => {
  const { dm_coso_id, nam_hoc, dm_truong_id } = useCommonSelectedHook();
  const { translate, checkAccesiableTo } = useCommonContext();
  const { status } = useDangKyAnHocSinh();
  const [isSearch, setIsSearch] = useState(false);
  const [foBaoCaoAns, setFoBaoCaoAns] = useState<any[]>([]);
  const [filterTruongId, setFilterTruongId] = useState<number>(0);
  const [filterKhoiId, setFilterKhoiId] = useState<number>(0);
  const [filterHeId, setFilterHeId] = useState<number>(0);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const canView = checkAccesiableTo(`${FO_BAOCAO_AN_HOC_SINH_API_END_POINT}/thang`, "POST");

  const [dataLoad, setDataLoad] = useState<IFoThucDonItemRequest>({
    dm_coso_id,
    dm_truong_id,
    from_date: dayjs().startOf("month").format("YYYY-MM-DD"),
    to_date: new Date().toISOString().split("T")[0],
  });

  const getDayColumns = (fromDateStr: string, toDateStr: string): string[] => {
    const from = dayjs(fromDateStr);
    const to = dayjs(toDateStr);
    const days: string[] = [];

    let current = from.startOf("day");
    while (current.isSameOrBefore(to, "day")) {
      days.push(current.format("DD"));
      current = current.add(1, "day");
    }

    return days;
  };

  const handleDateChange = (field: "from_date" | "to_date") =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDataLoad((prev) => ({ ...prev, [field]: e.target.value }));
      setIsSearch(false)
    };

  const calculateSummaryFinal = (student: any, dayRange: string[]) => {
    student.di_hoc = 0;
    student.nghi_phep = 0;
    student.vang = 0;
    student.cas = 0;
    student.kas = 0;
    student.ka = 0;

    dayRange.forEach((dayStr) => {
      const key = `ngay_${dayStr}`;
      const value = student[key];

      if (!value || value === "–") {
        student[key] = "–";
        return;
      }

      switch (value) {
        case "1":
          student.di_hoc++;
          const combo = student.comboByDay?.[dayStr];
          if (combo === 1) student.cas++;
          if (combo === 2) student.kas++;
          if (combo === 3) student.ka++;
          break;

        case "P":
          student.nghi_phep++;
          break;

        case "V":
          student.vang++;
          break;
      }
    });

    // Giống BE: Ngày tính phí = (1 + V)
    student.ngay_tinh_phi = student.di_hoc + student.vang;

    return student;
  };

  const handleSearchUserAsync = async () => {
    if (!dm_coso_id || !nam_hoc || !dataLoad.from_date || !dataLoad.to_date)
      return;

    setIsSearch(true)

    try {
      const [res, resDiemDanh] = await Promise.all([
        foDangKyAnHocSinhApi.select_by_baocao_thang({
          dm_coso_id,
          dm_truong_id: filterTruongId,
          nam_hoc,
          from_date: dataLoad.from_date,
          to_date: dataLoad.to_date,
        }),
        foDangKyAnHocSinhApi.select_by_baocao_diemdanh({
          dm_coso_id,
          dm_truong_id: filterTruongId,
          nam_hoc,
          from_date: dataLoad.from_date,
          to_date: dataLoad.to_date,
        })
      ]);

      if (!res.is_success) return;

      const rawData = res.data || [];
      const diemDanhData = resDiemDanh.data || [];

      const dayRange = getDayColumns(dataLoad.from_date, dataLoad.to_date);

      // --- GROUP THEO HỌC SINH ---
      const groupedData: Record<number, any> = {};

      rawData.forEach((record: any) => {
        const studentId = record.ts_hocsinh_id;
        const dayStr = dayjs(record.ngay_dang_ky).format("DD");

        if (!groupedData[studentId]) {
          groupedData[studentId] = {
            ...record,
            comboByDay: {},
          };
        }

        const student = groupedData[studentId];
        student.comboByDay[dayStr] = record.fo_xacnhanan_combo_id;

        // Mặc định: chỉ combo 1,2 → ăn → status = 1
        if ([1, 2].includes(record.fo_xacnhanan_combo_id)) {
          student[`ngay_${dayStr}`] = "1";
        } else if (record.fo_xacnhanan_combo_id === 3) {
          student[`ngay_${dayStr}`] = "–"; // KA
        }
      });

      // --- GỘP ĐIỂM DANH ---
      diemDanhData.forEach((item: any) => {
        const student = groupedData[item.ts_hocsinh_id];
        if (!student) return;

        const dayStr = dayjs(item.ngay_diem_danh).format("DD");
        ;
        const key = `ngay_${dayStr}`;

        // Không có đăng ký ăn → giữ "-"
        if (!student[key] || student[key] === "–") return;

        // Nếu FE đánh dấu 1 → BE cho phép ghi đè (P/V)
        switch (item.sis_diemdanh_status_id) {
          case 3:
            student[key] = item.is_hoan_phi_an_hop_le ? "P" : "V";
            break;
          case 4:
            student[key] = "V";
            break;
        }
      });

      // --- AUTO V: CÓ ĂN NHƯNG KHÔNG CÓ ĐIỂM DANH ---
      const diemDanhMap = new Set<string>();
      diemDanhData.forEach((item: any) => {
        const key = `${item.ts_hocsinh_id}_${dayjs(item.ngay_diem_danh).format("DD")}`;
        diemDanhMap.add(key);
      });

      Object.values(groupedData).forEach((student: any) => {
        dayRange.forEach((dayStr) => {
          const key = `ngay_${dayStr}`;
          const diemDanhKey = `${student.ts_hocsinh_id}_${dayStr}`;

          if (
            student[key] === "1" &&                     // có đăng ký ăn
            !diemDanhMap.has(diemDanhKey) &&             // không có điểm danh
            dayjs(
              `${dayStr}-${dayjs(dataLoad.from_date).format("MM-YYYY")}`,
              "DD-MM-YYYY"
            ).isBefore(dayjs(), "day")                   // ngày đã qua
          ) {
            student[key] = "V";
          }
        });
      });

      // --- TÍNH TỔNG THEO BE ---
      const transformed = Object.values(groupedData).map((student: any) =>
        calculateSummaryFinal(student, dayRange)
      );

      setFoBaoCaoAns(transformed);

    } catch (err) {
      console.error("handleSearchUserAsync error:", err);
    }
  };

  useEffect(() => {
    setFoBaoCaoAns([]);
    setIsSearch(false);
  }, [dm_coso_id, dm_truong_id, nam_hoc, dataLoad]);

  const filteredData = useMemo(() => {
    return foBaoCaoAns.filter((item) => {
      return (
        (!filterTruongId || item.dm_truong_id === filterTruongId) &&
        (!filterKhoiId || item.dm_khoi_id === filterKhoiId)
      );
    });
  }, [foBaoCaoAns, filterTruongId, filterKhoiId, filterHeId]);

  const generateColumns = useCallback((): IColumn[] => {
    const baseColumns: IColumn[] = [
      { caption: "Mã KH", dataField: "ma_kh", isMainColumn: true, fixed: true, width: 120 },
      { caption: "Mã HS", dataField: "ma_hs", isMainColumn: true, fixed: true, width: 120 },
      {
        dataField: "ho_ten",
        width: 200,
        caption: translate("Học sinh"),
        cellRender: (data) => <HocSinhShortInfo data={data} />,
      },
      { caption: "Khối", dataField: "ten_khoi", align: "center", fixed: true, width: 50 },
      { caption: "Lớp", dataField: "ten_lop", align: "center", fixed: true, width: 50 },
    ];

    const getDayColumns = (from: string, to: string): IColumn[] => {
      const fromDate = dayjs(from);
      const toDate = dayjs(to);
      const result: IColumn[] = [];

      let current = fromDate;
      while (current.isSameOrBefore(toDate, "day")) {
        const dayStr = current.format("DD");
        const fieldKey = `ngay_${dayStr}`;

        result.push({
          caption: dayStr,
          dataField: fieldKey,
          width: 30,
          align: "center",
          cellRender: (data: any) => (
            <TextWithEllipse text={data[fieldKey] ?? "–"} lineNumber={1} />
          ),
        });

        current = current.add(1, "day");
      }

      return result;
    };

    const dynamicDateColumns = getDayColumns(dataLoad.from_date, dataLoad.to_date);

    const summaryColumns: IColumn[] = [
      { caption: "CAS", dataField: "cas", width: 30, align: "center" },
      { caption: "KAS", dataField: "kas", width: 30, align: "center" },
      { caption: "KA", dataField: "ka", width: 30, align: "center" },
      { caption: "Đi học", dataField: "di_hoc", width: 50, align: "center" },
      { caption: "Nghỉ phép", dataField: "nghi_phep", width: 50, align: "center" },
      { caption: "Vắng", dataField: "vang", width: 50, align: "center" },
      { caption: "Tính phí", dataField: "ngay_tinh_phi", width: 50, align: "center" },
    ];

    return [...baseColumns, ...dynamicDateColumns, ...summaryColumns];

  }, [translate, dataLoad.from_date, dataLoad.to_date]);

  const handleExportExcelAccounting = async () => {
    try {
      setIsExporting(true);
      const res = await foDangKyAnHocSinhApi.exportExcelAccounting({
        dm_coso_id,
        dm_truong_id: filterTruongId,
        from_date: dataLoad.from_date,
        to_date: dataLoad.to_date,
        nam_hoc: nam_hoc
      });

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      setIsExporting(false);

      const timestamp = dayjs().format("YYYYMMDD_HHmmss");
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `BaoCaoKeToan_${timestamp}.xlsx`;
      a.click();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export Excel failed:", error);
    }
  };

  if (!canView) {
    return (
      <Box sx={{ textAlign: "center", padding: 4 }}>
        <Text text="Base.Message.NoPermission" />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <MyText text="Từ ngày" sx={{ mb: 1, fontWeight: "600", fontSize: "13px", color: "fg.default", mr: 2 }} />
          <MyTextInput
            type="date"
            sx={{ width: "180px", height: "30px", borderRadius: "6px", border: "1px solid", borderColor: "border.default", "&:focus": { borderColor: "accent.emphasis", boxShadow: "0 0 0 2px rgba(3, 102, 214, 0.2)" } }}
            value={dataLoad.from_date}
            onChange={handleDateChange("from_date")}
          />
        </Box>
        <Box>
          <MyText text="Đến ngày" sx={{ mb: 1, fontWeight: "600", fontSize: "13px", color: "fg.default", mr: 2 }} />
          <MyTextInput
            type="date"
            sx={{ width: "180px", height: "30px", borderRadius: "6px", border: "1px solid", borderColor: "border.default", "&:focus": { borderColor: "accent.emphasis", boxShadow: "0 0 0 2px rgba(3, 102, 214, 0.2)" } }}
            value={dataLoad.to_date}
            onChange={handleDateChange("to_date")}
          />
        </Box>
        <Box>
          <Button
            text="Tìm kiếm"
            variant="primary"
            leadingVisual={SearchIcon}
            onClick={handleSearchUserAsync}
          />
        </Box>
        <Box sx={{ color: 'red', fontStyle: 'italic' }}>{!isSearch ? "(Nhấn nút tìm kiếm để tìm dữ liệu)" : ""}</Box>
      </Box>
      <DataTable
        data={filteredData}
        isLoading={status === ePageBaseStatus.is_loading}
        title="Báo cáo ăn theo thời gian"
        height="calc(100vh - 240px)"
        exportEnable
        actionComponent={
          <>
            <Box sx={{ marginRight: 2 }}>
              <ComboboxTruong value={filterTruongId} onValueChanged={(id) => { setFilterTruongId(id); setFilterKhoiId(0); setFilterHeId(0); }} isShowClearButton />
            </Box>
            <Box sx={{ marginRight: 2 }}>
              <ComboboxKhoi value={filterKhoiId} onValueChanged={(id) => { setFilterKhoiId(id); setFilterHeId(0); }} dm_truong_id={filterTruongId || undefined} isShowClearButton />
            </Box>
            <Button text="Base.Label.Refresh" leadingVisual={SyncIcon} onClick={handleSearchUserAsync} />
            <Button
              text="Báo cáo kế toán"
              leadingVisual={FileDirectoryIcon}
              onClick={handleExportExcelAccounting}
              sx={{ marginLeft: 2 }}
              loading={isExporting}
            />
          </>
        }
        columns={generateColumns()}
        paging={{ enable: true, pageSizeItems: [50, 200, 500, 1000, 5000] }}
      />
    </Box>
  );
};

export default BaoCaoThangPage;